import { type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useBetslipContext } from '@cx-web/feature-betslip/context';
import { getOddsByFormat } from '@cx-web/feature-odds-format';
import { useRepubContext } from '@cx-web/feature-repub';
import clsx from 'clsx';

import { type Bet } from '~/components/BettingInsights/types';
import { useBetslip } from '~/hooks/useBetslip.tsx';
import { useClickoutBet } from '~/hooks/useClickoutBet.tsx';
import { OddsButton, type OddsButtonProps } from '~/vendor/cxy/components/OddsButton';

import styles from './BetButton.module.css';

export type BetButtonProps = Omit<OddsButtonProps, 'odds'> & {
  bet: Bet;
  buttonClassName?: string;
  buttonStyles?: CSSProperties;
  label?: string;
  locked?: boolean;
  showLabel?: boolean;
  unformattedOdds: {
    oddsAmerican: string;
    oddsDecimal: number;
    oddsFractional: string;
  };
};

export const BetButton = ({
  bet,
  buttonClassName,
  buttonStyles,
  className,
  hideBookieLogo,
  label,
  locked,
  onClick,
  showLabel = true,
  unformattedOdds,
  ...props
}: BetButtonProps) => {
  const { bookieName } = props;
  const { repubIs } = useRepubContext();
  const { userOddsFormat } = useBetslipContext();
  const { t } = useTranslation();

  // WS is clickout-only (no betslip); every other repub drives the unified betslip interface, which
  // hides the OC vs non-OC split behind one surface.
  const clickoutBet = useClickoutBet({ bet, onClick });
  const betslip = useBetslip({ bet, onClick, bookieName });

  const { handleClick, isBetInBetslip } = repubIs.WS ? clickoutBet : betslip;

  const odds = getOddsByFormat(unformattedOdds, userOddsFormat, bet.bookmakerCode);

  return (
    <OddsButton
      aria-pressed={isBetInBetslip}
      data-state={isBetInBetslip ? 'on' : 'off'}
      hideBookieLogo={hideBookieLogo}
      locked={locked}
      odds={odds}
      onClick={locked ? undefined : handleClick}
      selected={isBetInBetslip}
      wrapperClassName={className}
      wrapperStyles={buttonStyles}
      {...props}
      className={clsx(styles.betButton, buttonClassName, {
        [styles.isSelected]: isBetInBetslip,
      })}
      label={showLabel ? label || t('BEST_ODDS') : undefined}
    />
  );
};
